import { useState, type KeyboardEvent } from 'react';
import { DragHandle } from './DragHandle';

export interface ChecklistItem {
  id: string;
  text: string;
  met: boolean;
}

export interface ChecklistProps {
  items: ChecklistItem[];
  /** Toggle a row's met state. */
  onToggle: (id: string) => void;
  /** A row's text changed (per keystroke — parent debounces persistence). */
  onChangeText: (id: string, text: string) => void;
  /** Flush the current text (row blur). */
  onCommitText: () => void;
  /** Remove a row. */
  onRemove: (id: string) => void;
  /** Persist a new top-to-bottom order. */
  onReorder: (orderedIds: string[]) => void;
  /** Append a blank row. */
  onAdd: () => void;
  addLabel?: string;
  textPlaceholder?: string;
  textAriaLabel?: string;
  /** Whether to show the "x of N complete" progress header (default true). */
  progress?: boolean;
  rowTestId?: (id: string) => string;
  addTestId?: string;
}

function reorder(ids: string[], fromId: string, toId: string): string[] {
  const from = ids.indexOf(fromId);
  const to = ids.indexOf(toId);
  if (from < 0 || to < 0 || from === to) return ids;
  const next = ids.slice();
  next.splice(from, 1);
  next.splice(to, 0, fromId);
  return next;
}

function swap(ids: string[], id: string, dir: -1 | 1): string[] {
  const i = ids.indexOf(id);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= ids.length) return ids;
  const next = ids.slice();
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}

/**
 * A Definition-of-Done style checklist: a progress header plus reorderable rows
 * with a custom check, strikethrough-when-met editable text, a drag grip, and a
 * hover-revealed remove. Rows reorder by mouse drag (the grip is the source) or
 * keyboard (focus the grip, ArrowUp / ArrowDown). Generic primitive — the
 * project DoD is the first consumer. CSS namespace: `pn-checklist`.
 */
export function Checklist({
  items,
  onToggle,
  onChangeText,
  onCommitText,
  onRemove,
  onReorder,
  onAdd,
  addLabel = '+ Add criterion',
  textPlaceholder = 'Criterion…',
  textAriaLabel = 'Criterion text',
  progress = true,
  rowTestId,
  addTestId
}: ChecklistProps) {
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  const ids = items.map((i) => i.id);
  const done = items.filter((i) => i.met).length;
  const total = items.length;
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);

  const onGripKey = (e: KeyboardEvent<HTMLSpanElement>, id: string) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      onReorder(swap(ids, id, -1));
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      onReorder(swap(ids, id, 1));
    }
  };

  return (
    <div className="pn-checklist">
      {progress && total > 0 ? (
        <div className="pn-checklist-progress">
          <span className="pn-checklist-count">
            <b>{done}</b> of {total} complete
          </span>
          <span className="pn-checklist-bar" aria-hidden="true">
            <i style={{ width: `${pct}%` }} />
          </span>
        </div>
      ) : null}

      <div className="pn-checklist-list">
        {items.map((c) => (
          <div
            key={c.id}
            className="pn-checklist-row"
            data-met={c.met ? 'true' : 'false'}
            data-dragging={dragId === c.id ? 'true' : undefined}
            data-drop={overId === c.id && dragId !== c.id ? 'true' : undefined}
            data-testid={rowTestId ? rowTestId(c.id) : undefined}
            onDragOver={(e) => {
              if (!dragId) return;
              e.preventDefault();
              if (overId !== c.id) setOverId(c.id);
            }}
            onDrop={(e) => {
              if (!dragId) return;
              e.preventDefault();
              if (dragId !== c.id) onReorder(reorder(ids, dragId, c.id));
              setDragId(null);
              setOverId(null);
            }}
          >
            <DragHandle
              aria-label={`Reorder ${c.text || 'criterion'}`}
              dragging={dragId === c.id}
              onDragStart={(e) => {
                setDragId(c.id);
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', c.id);
              }}
              onDragEnd={() => {
                setDragId(null);
                setOverId(null);
              }}
              onKeyDown={(e) => onGripKey(e, c.id)}
            />
            <button
              type="button"
              className="pn-checklist-check"
              role="checkbox"
              aria-checked={c.met}
              aria-label={`Met: ${c.text || 'criterion'}`}
              onClick={() => onToggle(c.id)}
            />
            <input
              className="pn-checklist-text"
              value={c.text}
              placeholder={textPlaceholder}
              aria-label={textAriaLabel}
              onChange={(e) => onChangeText(c.id, e.currentTarget.value)}
              onBlur={onCommitText}
            />
            <button
              type="button"
              className="pn-checklist-remove"
              aria-label="Remove criterion"
              onClick={() => onRemove(c.id)}
            >
              ×
            </button>
          </div>
        ))}
      </div>

      <button
        type="button"
        className="pn-checklist-add"
        onClick={onAdd}
        data-testid={addTestId}
      >
        {addLabel}
      </button>
    </div>
  );
}
